/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Master Server Configuration Service
 * 
 * Provides configuration for remote authentication master server,
 * including URL resolution and endpoint constants.
 */

import { getEnv } from '../utils/env';

// Default master server URL (hardcoded fallback)
const DEFAULT_MASTER_SERVER_URL = 'https://auth.velocityapp.ai';

/**
 * Get the master server URL
 * 
 * Priority order:
 * 1. Electron-injected config (window.__REMOTE_AUTH_CONFIG__)
 * 2. VELOCITY_MASTER_SERVER_URL environment variable
 * 3. Hardcoded default (https://auth.velocityapp.ai)
 * 
 * Uses the standardized getEnv() interface which handles all contexts.
 * 
 * @returns The master server URL
 */
export function getMasterServerUrl(): string {
  return getEnv('VELOCITY_MASTER_SERVER_URL', DEFAULT_MASTER_SERVER_URL)!;
}

/**
 * Master server authentication endpoints
 * 
 * These endpoints are relative paths that will be appended to the master server URL
 */
export const MASTER_AUTH_ENDPOINTS = {
  LOGIN: '/api/auth/login',
  REGISTER: '/api/auth/register',
  REFRESH: '/api/auth/refresh',
  ME: '/api/auth/me',
  LOGOUT: '/api/auth/logout',
  CHANGE_PASSWORD: '/api/auth/change-password',
  UPDATE_PROFILE: '/api/auth/profile',
  FIREBASE_VERIFY: '/api/auth/firebase/verify',
  VALIDATE_ACCOUNT: '/api/auth/validate-account',
  ORGANIZATIONS: '/api/organizations',
} as const;

/**
 * Get the full URL for a master server authentication endpoint
 * 
 * @param endpoint The endpoint key from MASTER_AUTH_ENDPOINTS
 * @returns The full URL for the endpoint
 */
export function getMasterAuthEndpointUrl(endpoint: keyof typeof MASTER_AUTH_ENDPOINTS): string {
  const baseUrl = getMasterServerUrl();
  const path = MASTER_AUTH_ENDPOINTS[endpoint];
  
  // Ensure no double slashes
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  return `${normalizedBaseUrl}${path}`;
}

/**
 * Check if the master server URL is the default or custom
 * 
 * @returns true if using the default master server URL
 */
export function isUsingDefaultMasterServer(): boolean {
  return getMasterServerUrl() === DEFAULT_MASTER_SERVER_URL;
}

